import { LitElement, html, css } from 'lit';

/** De veldapp in Google Play; daar komen ook de updates vandaan. */
const PLAY_URL = 'https://play.google.com/store/apps/details?id=nl.geovoice.app';

export class RecorderApp extends LitElement {
    static styles = css`
        :host { display: block; }

        .page {
            max-width: 860px;
        }

        h1 {
            margin: 0 0 0.35rem;
            font-size: 1.5rem;
            font-weight: 700;
            color: var(--mv-text);
        }

        .subtitle {
            color: var(--mv-text-muted);
            margin: 0 0 2rem;
            font-size: 0.9rem;
        }

        .body {
            display: flex;
            gap: 2rem;
            align-items: flex-start;
        }

        /* ── Left column ── */
        .left {
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            width: 220px;
        }

        .qr-card {
            background: var(--mv-surface);
            border-radius: 16px;
            border: 1px solid var(--mv-border);
            padding: 1.25rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 0.875rem;
            box-shadow: var(--mv-shadow-sm);
        }

        .qr-card img {
            display: block;
            width: 168px;
            height: 168px;
            border-radius: 8px;
        }

        .qr-label {
            font-size: 0.75rem;
            color: var(--mv-text-muted);
            margin-top: 10px;
            letter-spacing: 0.01em;
        }

        .download-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            background: var(--mv-primary);
            color: white;
            border-radius: 10px;
            padding: 11px 0;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            text-decoration: none;
            transition: background 0.15s;
        }

        .download-btn:hover { background: var(--mv-primary-hover); }

        /* Geen fout, wel iets wat je moet weten voordat je iemand de app laat installeren. */
        .platform {
            display: flex;
            gap: 0.75rem;
            align-items: flex-start;
            margin: 0 0 1.5rem;
            padding: 0.875rem 1rem;
            border-radius: 12px;
            border: 1px solid var(--mv-warning-panel-border);
            background: var(--mv-warning-panel-bg);
            color: var(--mv-warning-text);
        }

        .platform svg { flex-shrink: 0; margin-top: 2px; }
        .platform strong { display: block; font-size: 0.9rem; margin-bottom: 2px; }
        .platform p { margin: 0; font-size: 0.85rem; line-height: 1.5; }

        .download-icon {
            width: 15px;
            height: 15px;
            stroke: white;
            fill: none;
            stroke-width: 2;
            stroke-linecap: round;
            stroke-linejoin: round;
            flex-shrink: 0;
        }

        /* ── Right column ── */
        .steps {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        .step {
            display: flex;
            align-items: flex-start;
            gap: 0.875rem;
            background: var(--mv-surface);
            border-radius: 12px;
            padding: 1rem 1.125rem;
            border: 1px solid var(--mv-border);
            box-shadow: var(--mv-shadow-sm);
            transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .step:hover {
            transform: translateY(-2px);
            box-shadow: var(--mv-shadow);
            border-color: var(--mv-primary);
        }

        .step-num {
            width: 26px;
            height: 26px;
            border-radius: 50%;
            background: var(--mv-primary-tint);
            color: var(--mv-primary);
            font-weight: 700;
            font-size: 0.775rem;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            margin-top: 1px;
        }

        .step-body h4 {
            margin: 0 0 3px;
            font-size: 0.875rem;
            font-weight: 600;
            color: var(--mv-text);
        }

        .step-body p {
            margin: 0;
            font-size: 0.825rem;
            color: var(--mv-text-sub);
            line-height: 1.55;
        }

        @media (max-width: 600px) {
            .body { flex-direction: column; }
            .left { width: 100%; }
            .qr-card img { width: 140px; height: 140px; }
        }
    `;

    render() {
        const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
        const qrColor = isDark ? 'ffffff' : '000000';
        const qrBg = isDark ? '1e1e2d' : 'ffffff';
        const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=168x168&margin=0&color=${qrColor}&bgcolor=${qrBg}&data=${encodeURIComponent(PLAY_URL)}`;

        return html`
            <div class="page">
                <h1>Recorder App</h1>
                <p class="subtitle">De GeoVoice-app voor veldopnames. Scan de QR-code met je telefoon of tablet, of open de app in Google Play.</p>

                <div class="platform" role="note">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                    <div>
                        <strong>Alleen voor Android</strong>
                        <p>GeoVoice staat helaas nog niet in de Apple App Store. Daarom kun je de app op dit moment alleen gebruiken op een Android-telefoon of -tablet, niet op een iPhone of iPad.</p>
                    </div>
                </div>

                <div class="body">
                    <div class="left">
                        <div class="qr-card">
                            <img src="${qrSrc}" alt="QR-code naar GeoVoice in Google Play" />
                            <span class="qr-label">Scan om te installeren</span>
                        </div>
                        <a class="download-btn" href="${PLAY_URL}" target="_blank" rel="noopener noreferrer">
                            <svg class="download-icon" viewBox="0 0 24 24"><polygon points="6 3 20 12 6 21 6 3"/></svg>
                            Open in Google Play
                        </a>
                    </div>

                    <div class="steps">
                        <div class="step">
                            <div class="step-num">1</div>
                            <div class="step-body">
                                <h4>Installeer de app</h4>
                                <p>Installeer GeoVoice uit Google Play. Updates komen daarna vanzelf via de Play Store.</p>
                            </div>
                        </div>
                        <div class="step">
                            <div class="step-num">2</div>
                            <div class="step-body">
                                <h4>Log in</h4>
                                <p>Gebruik hetzelfde e-mailadres en wachtwoord als de backoffice.</p>
                            </div>
                        </div>
                        <div class="step">
                            <div class="step-num">3</div>
                            <div class="step-body">
                                <h4>Verbinding</h4>
                                <p>Opnames worden lokaal opgeslagen en automatisch gesynchroniseerd zodra er internet is.</p>
                            </div>
                        </div>
                        <div class="step">
                            <div class="step-num">4</div>
                            <div class="step-body">
                                <h4>Opnemen</h4>
                                <p>Selecteer een project en houd de PTT-knop ingedrukt om te starten. Loslaten stopt de opname.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('recorder-app', RecorderApp);
